import { apiService } from '../../utils/api';
import type { ApprovalDiff } from './components/compare/types';
import type {
  Approver,
  ApprovalContext,
  ApprovalExemption,
  ApprovalRequest,
  ApprovalSummary,
  ObjectType,
} from './types';

// Schvalovací workflow — tenký wrapper nad apiService (X-Employee-ID doplňuje interceptor).

// List endpoints may return a paginated envelope instead of a plain array.
const approvalSettingsList = <T>(data: unknown): T[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object' && 'results' in data && Array.isArray(data.results)) {
    return data.results;
  }
  throw new Error('Neplatný formát seznamu nastavení schvalování.');
};

// --- Kontext workflow (dostupné každému ověřenému zaměstnanci) ---
export const getApprovalContext = async (): Promise<ApprovalContext> =>
  (await apiService.get('/api/v1/approvals/context/')).data;

// --- Přepínač workflow ---
export const toggleApproval = (companyId: string, enabled: boolean) =>
  apiService.patch<{ solution_approval_required: boolean }>(
    `/api/v1/company/${companyId}/approval-toggle/`,
    { enabled }
  );

// --- Schvalovatelé ---
export const listApprovers = async (): Promise<Approver[]> =>
  approvalSettingsList<Approver>((await apiService.get('/api/v1/approvers/')).data);

export const createApprover = (payload: {
  employee_id: string;
  classification_id: string | null;
}) => apiService.post('/api/v1/approvers/', payload);

export const deleteApprover = (id: string) => apiService.delete(`/api/v1/approvers/${id}/`);

// --- Výjimky ---
export const listExemptions = async (): Promise<ApprovalExemption[]> =>
  approvalSettingsList<ApprovalExemption>(
    (await apiService.get('/api/v1/approval-exemptions/')).data
  );

export const createExemption = (payload: { classification_id: string }) =>
  apiService.post('/api/v1/approval-exemptions/', payload);

export const deleteExemption = (id: string) =>
  apiService.delete(`/api/v1/approval-exemptions/${id}/`);

// --- Žádosti ---
export const listApprovals = async (params: {
  status?: string;
  objectType?: string;
  page?: number;
  page_size?: number;
}) => (await apiService.get('/api/v1/approvals/', { params })).data;

export const getApproval = async (id: string): Promise<ApprovalRequest> =>
  (await apiService.get(`/api/v1/approvals/${id}/`)).data;

// Paginated endpoint (CorePageNumberPagination) — unwrap `results`.
export const listMyApprovals = async (): Promise<ApprovalRequest[]> =>
  (await apiService.get('/api/v1/approvals/my/', { params: { page_size: 100 } })).data.results ??
  [];

/** Live (draft/pending/rejected) request for an item regardless of author, or null. */
export const getApprovalForObject = async (
  objectType: ObjectType,
  objectId: string
): Promise<ApprovalRequest | null> =>
  (await apiService.get('/api/v1/approvals/for-object/', { params: { objectType, objectId } }))
    .data;

export const getApprovalSummary = async (): Promise<ApprovalSummary> =>
  (await apiService.get('/api/v1/approvals/summary/')).data;

export const submitApproval = (id: string) =>
  apiService.post(`/api/v1/approvals/${id}/submit/`, {});

export const approveApproval = (id: string) =>
  apiService.post(`/api/v1/approvals/${id}/approve/`, {});

export const rejectApproval = (id: string, comment: string) =>
  apiService.post(`/api/v1/approvals/${id}/reject/`, { comment });

export const commentApproval = (id: string, comment: string) =>
  apiService.post(`/api/v1/approvals/${id}/comment/`, { comment });

/**
 * Diff of the submitted version against a base (default: last approved version).
 * API failures must never substitute demo data for an actual approval decision.
 */
export const getApprovalDiff = async (
  request: ApprovalRequest,
  baseVersionId?: string
): Promise<ApprovalDiff> => {
  return (
    await apiService.get(`/api/v1/approvals/${request.id}/diff/`, {
      params: baseVersionId ? { base: baseVersionId } : undefined,
    })
  ).data;
};

// --- Adopce komunitní verze ---
export const adoptCommunityVersion = (payload: { object_type: ObjectType; object_id: string }) =>
  apiService.post('/api/v1/approvals/adopt/', payload);
